import {
  Button,
  InlineAlert,
  Page,
  PageActions,
  PageContent,
  PageDescription,
  PageHeader,
  PageTitle,
  PageTitleBlock,
  Skeleton,
  Stat,
  StatGrid,
} from '@planuze/ui';
import { Link } from '@tanstack/react-router';
import {
  ArrowRightIcon,
  BadgeCheckIcon,
  BarChart3,
  Building2,
  DollarSign,
  MessageSquare,
  PackageCheck,
  ShieldAlertIcon,
  TrendingDown,
  UserPlus,
  Users,
} from 'lucide-react';
import type { JSX } from 'react';
import { useDashboardContent } from '../content/sections/dashboard.js';
import { useMetricsOverview, useModerationMetrics } from '../hooks/use-metrics.js';
import { useStaffSession } from '../hooks/use-staff-session.js';
import { fmtCurrency, fmtNumber, fmtPercent } from '../lib/format.js';

interface Shortcut {
  readonly to: '/companies' | '/publishers' | '/pack-submissions' | '/support' | '/metrics';
  readonly label: string;
  readonly description: string;
  readonly icon: typeof Building2;
  readonly moderateOnly?: boolean;
}

/* Painel — landing pós-login (ADR-0700). Overview de KPIs (receita, orgs, signups, atividade,
   churn, fila de moderação) + atalhos para as seções. O Metrics segue como a análise detalhada
   com filtros/séries; aqui é o snapshot + navegação. */
export function DashboardPage(): JSX.Element {
  const session = useStaffSession();
  const copy = useDashboardContent();
  const canModerate = session?.role === 'super_admin' || session?.role === 'support';
  const overview = useMetricsOverview();
  const moderation = useModerationMetrics();
  /* O literal é anotado ANTES do filtro. Com a anotação só na variável final, o `.filter()`
     no fim rompe a tipagem contextual: o array é inferido sozinho (e `to` alarga de união
     literal para `string`) e só então é atribuído — que é o erro que aparecia aqui. */
  const todosAtalhos: readonly Shortcut[] = [
    {
      to: '/companies',
      label: copy.shortcuts.companiesLabel,
      description: copy.shortcuts.companiesDescription,
      icon: Building2,
    },
    {
      to: '/publishers',
      label: copy.shortcuts.publishersLabel,
      description: copy.shortcuts.publishersDescription,
      icon: BadgeCheckIcon,
      moderateOnly: true,
    },
    {
      to: '/pack-submissions',
      label: copy.shortcuts.moderationLabel,
      description: copy.shortcuts.moderationDescription,
      icon: PackageCheck,
      moderateOnly: true,
    },
    {
      to: '/support',
      label: copy.shortcuts.supportLabel,
      description: copy.shortcuts.supportDescription,
      icon: MessageSquare,
      moderateOnly: true,
    },
    {
      to: '/metrics',
      label: copy.shortcuts.metricsLabel,
      description: copy.shortcuts.metricsDescription,
      icon: BarChart3,
    },
  ];
  const shortcuts = todosAtalhos.filter((s) => s.moderateOnly !== true || canModerate);

  return (
    <Page>
      <PageHeader>
        <PageTitleBlock>
          <PageTitle>{copy.title}</PageTitle>
          <PageDescription>{copy.description}</PageDescription>
        </PageTitleBlock>
        <PageActions>
          <Button asChild variant="outline" size="sm">
            <Link to="/metrics">
              {copy.viewMetrics}
              <ArrowRightIcon className="size-4" aria-hidden="true" />
            </Link>
          </Button>
        </PageActions>
      </PageHeader>
      <PageContent className="space-y-8">
        {overview.isError ? (
          <InlineAlert variant="destructive">{copy.loadFailed}</InlineAlert>
        ) : overview.data === undefined ? (
          <StatGrid columns={4}>
            {['a', 'b', 'c', 'd', 'e', 'f', 'g', 'h'].map((k) => (
              <Skeleton key={k} className="h-[92px] rounded-lg" />
            ))}
          </StatGrid>
        ) : (
          <StatGrid columns={4}>
            <Stat
              label={copy.stats.mrr}
              value={fmtCurrency(overview.data.mrrUsd, 'USD')}
              hint={fmtCurrency(overview.data.mrrBrl, 'BRL')}
              icon={<DollarSign />}
            />
            <Stat
              label={copy.stats.organizations}
              value={fmtNumber(overview.data.totalOrgs)}
              hint={copy.stats.blockedOrgs(fmtNumber(overview.data.blockedOrgs))}
              icon={<Building2 />}
            />
            <Stat
              label={copy.stats.signups30d}
              value={fmtNumber(overview.data.signups30d)}
              hint={copy.stats.signups7d(fmtNumber(overview.data.signups7d))}
              icon={<UserPlus />}
            />
            <Stat
              label={copy.stats.activeUsers24h}
              value={fmtNumber(overview.data.activeUsers24h)}
              hint={copy.stats.activeDevices(fmtNumber(overview.data.activeDevices24h))}
              icon={<Users />}
            />
            <Stat
              label={copy.stats.churn30d}
              value={fmtPercent(overview.data.churn30dPct)}
              icon={<TrendingDown />}
            />
            {canModerate && moderation.data !== undefined && (
              <Stat
                label={copy.stats.moderationQueue}
                value={fmtNumber(moderation.data.queueDepth)}
                hint={copy.stats.aboveSla(fmtNumber(moderation.data.aboveSlaCount))}
                icon={<ShieldAlertIcon />}
              />
            )}
          </StatGrid>
        )}

        <section className="space-y-3">
          <h2 className="text-sm font-semibold text-foreground">{copy.quickAccess}</h2>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {shortcuts.map((s) => {
              const Icon = s.icon;
              return (
                <Link
                  key={s.to}
                  to={s.to}
                  className="group flex items-center gap-3 rounded-lg border border-border bg-card p-4 transition-colors hover:border-primary/40 hover:bg-accent/10"
                >
                  <span className="flex size-10 shrink-0 items-center justify-center rounded-md bg-accent/10 text-accent">
                    <Icon className="size-5" aria-hidden="true" />
                  </span>
                  <span className="min-w-0">
                    <span className="block text-sm font-medium text-foreground">{s.label}</span>
                    <span className="block truncate text-xs text-muted-foreground">
                      {s.description}
                    </span>
                  </span>
                  <ArrowRightIcon
                    className="ml-auto size-4 shrink-0 text-muted-foreground/50 transition-transform group-hover:translate-x-0.5 group-hover:text-foreground"
                    aria-hidden="true"
                  />
                </Link>
              );
            })}
          </div>
        </section>
      </PageContent>
    </Page>
  );
}
